import React from 'react'
import { useMantineTheme } from '@mantine/core'
import AsyncCreatableSelect from 'react-select/async-creatable'
import debounce from 'lodash.debounce'

const LookupSelect = ({
	label,
	value,
	onChange,
	fetchOptions,
	onCreateNew,
	getOptionLabel,
	getOptionValue,
	placeholder = 'Select...',
	isClearable = true,
}) => {
	const theme = useMantineTheme()
	const isDark = theme.colorScheme === 'dark'
	const selectStyles = {
		control: (base, state) => ({
			...base,
			backgroundColor: isDark ? theme.colors.dark[6] : theme.white,
			borderColor: state.isFocused
				? theme.colors[theme.primaryColor][6]
				: isDark
					? theme.colors.dark[4]
					: theme.colors.gray[4],
			boxShadow: state.isFocused
				? `0 0 0 1px ${theme.colors[theme.primaryColor][6]}`
				: 'none',
			'&:hover': {
				borderColor: state.isFocused
					? theme.colors[theme.primaryColor][6]
					: isDark
						? theme.colors.dark[3]
						: theme.colors.gray[5],
			},
		}),
		input: (base) => ({
			...base,
			color: isDark ? theme.colors.dark[0] : theme.black,
		}),
		singleValue: (base) => ({
			...base,
			color: isDark ? theme.colors.dark[0] : theme.black,
		}),
		placeholder: (base) => ({
			...base,
			color: isDark ? theme.colors.dark[1] : theme.colors.gray[6],
		}),
		menu: (base) => ({
			...base,
			backgroundColor: isDark ? theme.colors.dark[6] : theme.white,
			border: `1px solid ${
				isDark ? theme.colors.dark[4] : theme.colors.gray[3]
			}`,
		}),
		option: (base, state) => ({
			...base,
			backgroundColor: state.isSelected
				? theme.colors[theme.primaryColor][7]
				: state.isFocused
					? isDark
						? theme.colors.dark[5]
						: theme.colors.gray[1]
					: isDark
						? theme.colors.dark[6]
						: theme.white,
			color: state.isSelected
				? theme.white
				: isDark
					? theme.colors.dark[0]
					: theme.black,
			':active': {
				backgroundColor: theme.colors[theme.primaryColor][7],
			},
		}),
		indicatorSeparator: (base) => ({
			...base,
			backgroundColor: isDark ? theme.colors.dark[4] : theme.colors.gray[3],
		}),
		dropdownIndicator: (base) => ({
			...base,
			color: isDark ? theme.colors.dark[1] : theme.colors.gray[5],
			'&:hover': {
				color: isDark ? theme.colors.dark[0] : theme.colors.gray[7],
			},
		}),
		clearIndicator: (base) => ({
			...base,
			color: isDark ? theme.colors.dark[1] : theme.colors.gray[5],
			'&:hover': {
				color: isDark ? theme.colors.dark[0] : theme.colors.gray[7],
			},
		}),
	}

	// Map a value object to a react-select compatible format
	const toOption = (item) => ({
		label: getOptionLabel(item),
		value: getOptionValue(item),
		data: item,
	})

	// Load options with debounce
	const loadOptions = debounce(async (input, callback) => {
		try {
			const results = await fetchOptions(input)
			callback(results.map(toOption))
		} catch (err) {
			console.error('LookupSelect fetchOptions failed:', err)
			callback([])
		}
	}, 300)

	// Handle selection
	const handleChange = (option) => {
		onChange(option ? option.data : null)
	}

	// Handle creation of new item
	const handleCreate = async (inputLabel) => {
		try {
			const newItem = await onCreateNew(inputLabel)
			onChange(newItem)
		} catch (err) {
			console.error('LookupSelect create failed:', err)
		}
	}

	return (
		<div style={{ marginBottom: '1rem' }}>
			{label && (
				<label style={{ display: 'block', marginBottom: '0.5rem' }}>
					{label}
				</label>
			)}
			<AsyncCreatableSelect
				placeholder={placeholder}
				value={value ? toOption(value) : null}
				onChange={handleChange}
				onCreateOption={handleCreate}
				loadOptions={loadOptions}
				defaultOptions
				isClearable={isClearable}
				styles={selectStyles}
			/>
		</div>
	)
}

export default LookupSelect
